/**
 * Ingresos y gastos: los inmuebles, para soltar tickets.
 *
 * ES LO PRIMERO QUE SE VE porque es lo que más se hace: llega una factura y se suelta en la
 * tarjeta de su inmueble. Sin elegir mes —cada ticket se lee y se apunta en el mes de su fecha—
 * y sin elegir inmueble en un desplegable: cada inmueble tiene su tarjeta, grande, y se puede
 * soltar encima o pulsarla para elegir los ficheros.
 *
 * LA CONCILIACIÓN TIENE SU PROPIA PÁGINA. Encima de las tarjetas, una franja dice cómo va el mes
 * y lleva a la tabla entera con «Ver conciliación». Juntas en una pantalla, con muchos
 * movimientos la tabla empujaba las tarjetas al fondo.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MONTH_NAMES_ES } from '@naar/shared';
import { api, queryKeys } from '../api/client';
import { useCan } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { Skeleton } from '../components/Skeleton';
import { MultiPropertyCard, PropertyCard } from './ledger/PropertyCard';
import { usePropertyCovers } from '../lib/usePropertyCovers';
import { filasDelMes, generalComoInmueble } from './ledger/reconciliation';
import cardStyles from './ledger/PropertyCards.module.css';

/**
 * El mes en curso según el calendario del usuario. Local y NO UTC: el 1 de junio a la 01:00 en
 * Madrid son todavía las 23:00 del 31 de mayo en UTC.
 */
function currentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function LedgerPage() {
  const navigate = useNavigate();
  const canManage = useCan('ledger:manage');
  const month = currentMonthKey();
  const mes = MONTH_NAMES_ES[Number(month.slice(5, 7)) - 1] ?? month;

  /*
   * El mes en curso: da los inmuebles y, de paso, cómo va la conciliación para la franja de
   * arriba. Es la misma consulta que la página de conciliación, así que comparten caché.
   */
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.ledgerMonth(month),
    queryFn: () => api.ledgerMonth(month),
  });

  const propiedades = data?.properties ?? [];
  const portadas = usePropertyCovers();
  const filas = data ? filasDelMes(data).filas : [];
  const pendientes = filas.filter((fila) => fila.estado === 'pendiente').length;
  const sinJustificar = filas.filter((fila) => fila.estado === 'sin_justificar').length;
  const mesesAtrasados = data?.pendingMonths.length ?? 0;

  const Mes = mes.charAt(0).toUpperCase() + mes.slice(1);
  const todoCompleto = filas.length > 0 && pendientes === 0 && sinJustificar === 0;

  return (
    <section>
      <PageHeader
        title="Ingresos y gastos"
        subtitle="Suelta los tickets en su inmueble según lleguen —varios a la vez, se ponen en cola—: se leen solos y se apuntan en el mes de su fecha."
        actions={
          /*
           * LA CONCILIACIÓN, EN UNA PÍLDORA junto al título: cómo va el mes en una frase y el botón
           * para entrar. Antes era una franja entera encima de las tarjetas que empujaba hacia
           * abajo lo que de verdad se usa aquí, que es soltar tickets.
           */
          <button type="button" className={cardStyles.pill} onClick={() => navigate('/ingresos-y-gastos/conciliacion')}>
            <span
              className={[
                cardStyles.pillDot,
                pendientes > 0 ? cardStyles.pillDotPendiente : todoCompleto ? cardStyles.pillDotOk : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden="true"
            />
            <span className={cardStyles.pillText}>
              {isPending || !data ? (
                'Cargando…'
              ) : filas.length === 0 ? (
                <>{Mes} · todavía sin movimientos</>
              ) : todoCompleto ? (
                <>
                  {Mes} · <b>todo completo</b>
                </>
              ) : (
                <>
                  {Mes} · <b>{pendientes > 0 ? `${pendientes} ${pendientes === 1 ? 'pendiente' : 'pendientes'}` : `${sinJustificar} sin justificar`}</b>{' '}
                  de {filas.length} {filas.length === 1 ? 'movimiento' : 'movimientos'}
                </>
              )}
              {mesesAtrasados > 0 &&
                ` · ${mesesAtrasados} ${mesesAtrasados === 1 ? 'mes anterior' : 'meses anteriores'} sin cuadrar`}
            </span>
            <span className={cardStyles.pillButton}>
              Ver conciliación
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </span>
          </button>
        }
      />

      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {/* --- Los inmuebles ------------------------------------------------------- */}
      <div className={cardStyles.section}>
        <span className={cardStyles.sectionLabel}>Inmuebles</span>
        {isPending ? (
          <div className={cardStyles.grid}>
            {[0, 1, 2].map((key) => (
              <Skeleton key={key} height={240} radius={8} />
            ))}
          </div>
        ) : data && propiedades.length === 0 ? (
          <EmptyState
            icon="building"
            title="Todavía no hay inmuebles"
            body="Los gastos de un piso se apuntan contra su inmueble. Da de alta el primero en Propiedades y aquí aparecerá su tarjeta."
          />
        ) : (
          <div className={cardStyles.grid}>
            {/* Las facturas de luz, agua o wifi de todos los pisos, de golpe: el piso lo dice cada una. */}
            {propiedades.length > 1 && (
              <MultiPropertyCard
                pisos={propiedades.map((property) => ({ id: property.id, name: property.name }))}
                canManage={canManage}
              />
            )}
            {propiedades.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                cover={portadas.get(property.id)}
                canManage={canManage}
              />
            ))}
          </div>
        )}
      </div>

      {/* --- Lo que no es de ningún inmueble: a todo el ancho --------------------- */}
      {data && (
        <div className={cardStyles.section}>
          <span className={cardStyles.sectionLabel}>De ningún inmueble</span>
          <PropertyCard property={generalComoInmueble(data)} canManage={canManage} layout="franja" />
        </div>
      )}
    </section>
  );
}
